import React, { useEffect, useState, useContext } from 'react';
import Select from 'react-select';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { UserContext } from "../UserContext";
import apiClient from '../../api/client';
import Header from '../Header'; // assuming you have a shared Header component
import ExpiryFilterChip from '../ExpiryOverview/ExpiryFilterChip';
import ExpiryDate from '../ExpiryDate';
import PhoneCell from '../PhoneCell';
import TruncatedText from '../TruncatedText';
import LoadError from '../LoadError';
import { getErrorMessage } from '../../utils/network';
import { getExpiryBucket } from '../../utils/expiry';
import Pagination from '../Pagination';
import './index.css';   // style this file for table responsiveness
import './compact.css'; // compact column widths (no stretched blank space)

/**
 * Every OTT provider shown on this page, in column order.
 *   field   -> users-table column holding its expiry
 *   pkg     -> key in get_operator_visible_packages.php (null = always shown)
 *   param   -> value used in ?provider= links from the dashboard
 */
const OTT_COLUMNS = [
  { param: 'vrplay', label: 'VRPLAY', field: 'ExpiryDate', pkg: null, tab: 'vrplay' },
  { param: 'etv', label: 'Etvwin', field: 'etvwin_expiry', pkg: 'etvwin', tab: 'etvwin' },
  { param: 'watcho', label: 'Watcho', field: 'watcho_expiry', pkg: 'watcho', tab: 'watcho' },
  { param: 'playboxtv', label: 'Playbox TV', field: 'playboxtv_expiry', pkg: 'playboxtv', tab: 'playboxtv' },
  { param: 'pioneeriptv', label: 'PioneerIptv', field: 'pioneeriptv_expiry', pkg: 'pioneeriptv', tab: 'pioneeriptv' },
  { param: 'antiptv', label: 'Antiptv', field: 'antiptv_expiry', pkg: 'antiptv', tab: 'antiptv' },
];

const DASHBOARD_LABELS = {
  vrplay: 'VRPlay', etv: 'ETV Win', watcho: 'Watcho', pioneeriptv: 'Pioneer IPTV',
  playboxtv: 'Playbox TV', antiptv: 'Antiptv',
};

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Highlights the search term without dangerouslySetInnerHTML (no XSS). */
const Highlight = ({ text, term }) => {
  const value = text == null ? '' : String(text);
  if (!term) return value;
  const parts = value.split(new RegExp(`(${escapeRegExp(term)})`, 'gi'));
  return parts.map((part, i) =>
    part.toLowerCase() === term.toLowerCase() ? <mark key={i}>{part}</mark> : <React.Fragment key={i}>{part}</React.Fragment>
  );
};

const CustomersList = () => {
  const { user, token } = useContext(UserContext);
  const [customers, setCustomers] = useState([]);
  const [operators, setOperators] = useState([]);
  const [selectedOperatorId, setSelectedOperatorId] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorCause, setErrorCause] = useState(null);
  // values like: all, vr_active, vr_inactive, watcho_active, ...
  const [filterStatus, setFilterStatus] = useState('all');
  // Sort by whichever expiry column header was clicked.
  const [sort, setSort] = useState({ field: null, asc: true });
  const [visibleColumns, setVisibleColumns] = useState({});

  // ?provider=watcho&expiry=today — set by the dashboard's expiry buttons.
  const [searchParams, setSearchParams] = useSearchParams();
  const expiryParam = searchParams.get('expiry');
  const providerParam = searchParams.get('provider') || 'vrplay';
  const expiryBucket = getExpiryBucket(expiryParam);
  const expiryColumn = OTT_COLUMNS.find((c) => c.param === providerParam) || OTT_COLUMNS[0];

  const navigate = useNavigate();
  const role = user.role;

  // Server-side pages (customers_page.php): filtering, sorting and counting happen in the
  // database; only one page of rows comes to the browser.
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(() => {
    try { return Number(sessionStorage.getItem('customers_per_page')) || 50; } catch { return 50; }
  });
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ total: 0, active: {} });
  const reqSeq = React.useRef(0);

  const fetchCustomers = async (p = page) => {
    const my = ++reqSeq.current;
    try {
      setLoading(true);
      const { data } = await apiClient.get('/customers_page.php', {
        params: {
          page: p,
          per_page: perPage,
          q: searchTerm,
          operator: selectedOperatorId,
          status: filterStatus,
          provider: expiryBucket ? providerParam : undefined,
          expiry: expiryBucket ? expiryBucket.key : undefined,
          sort: sort.field || undefined,
          dir: sort.field ? (sort.asc ? 'asc' : 'desc') : undefined,
        },
      });
      if (my !== reqSeq.current) return;
      if (!data.success) throw new Error(data.error || 'Failed to load customers');
      setCustomers(data.customers || []);
      setTotal(Number(data.total || 0));
      setSummary(data.summary || { total: 0, active: {} });
      if (data.page && data.page !== p) setPage(data.page);
      setError(null);
      setErrorCause(null);
    } catch (err) {
      if (my !== reqSeq.current) return;
      setError(getErrorMessage(err, 'Failed to load customers'));
      setErrorCause(err);
      setCustomers([]);
    } finally {
      if (my === reqSeq.current) setLoading(false);
    }
  };

  const fetchOperators = async () => {
    try {
      const { data } = await apiClient.get('/operator_api.php');
      setOperators(data.operators || []);
    } catch (err) {
      // Operator filter is a convenience; the list still works without it.
      console.error(err);
    }
  };

  const fetchVisibleColumns = async () => {
    try {
      const { data } = await apiClient.get("/customer/get_operator_visible_packages.php");
      if (data.success) setVisibleColumns(data.packages);
    } catch (err) {
      console.error(err);
    }
  };

  // Any filter / sort change: back to page 1
  useEffect(() => {
    setPage(1);
    fetchCustomers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedOperatorId, filterStatus, expiryParam, providerParam, sort, perPage]);

  const goPage = (p) => { setPage(p); fetchCustomers(p); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const changePerPage = (n) => {
    try { sessionStorage.setItem('customers_per_page', String(n)); } catch { /* ignore */ }
    setPerPage(n);
  };

  useEffect(() => {
    if (role !== "operator") fetchOperators();
  }, [role]);

  useEffect(() => {
    if (token) fetchVisibleColumns();
  }, [token]);

  // Debounce typing so filtering doesn't run on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput.trim()), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Opening from a dashboard expiry button: sort soonest-first on that column.
  useEffect(() => {
    if (expiryBucket) setSort({ field: expiryColumn.field, asc: true });
  }, [expiryParam, providerParam]); // eslint-disable-line react-hooks/exhaustive-deps

  // Focus mode = opened from a dashboard expiry button. Only that
  // provider's expiry is shown, and a row opens the customer page
  // straight on that provider's tab (e.g. /customer/12?tab=watcho).
  const focusMode = !!expiryBucket;
  const providerName = DASHBOARD_LABELS[expiryColumn.param];

  const shownColumns = focusMode
    ? [expiryColumn]
    : OTT_COLUMNS.filter((c) => c.pkg === null || visibleColumns[c.pkg]);

  const customerLink = (cust) =>
    focusMode ? `/customer/${cust.id}?tab=${expiryColumn.tab}` : `/customer/${cust.id}`;



  // Rows arrive filtered and sorted from the server.
  const filteredCustomers = customers;

  const toggleSort = (field) =>
    setSort((prev) => ({ field, asc: prev.field === field ? !prev.asc : true }));

  const sortArrow = (field) => (sort.field === field ? (sort.asc ? ' ▲' : ' ▼') : ' ↕');

  const clearExpiryFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('expiry');
    next.delete('provider');
    setSearchParams(next, { replace: true });
    navigate(`/${role}`); // back to dashboard
  };

  const operatorOptions = [
    { value: 'all', label: 'All Operators' },
    ...(role === "admin"
      ? [{
          value: user.operatorId,
          label: `${user.phoneNumber || user.operatorPhoneNumber || ''} - ${user.operatorName} (Admin)`
        }]
      : []),
    ...operators.map(op => ({
      value: op.id,
      label: `${op.phoneNumber || ''} - ${op.operatorName}`
    }))
  ];

  const activeCount = (field) => Number(summary.active?.[field] || 0);

  // Plain cell + coloured status pill (no more green/red row backgrounds).
  const renderExpiryCell = (key, value) => (
    <td key={key}><ExpiryDate value={value} /></td>
  );

  return (
    <>
      <Header />

      <div className="operator-header">
        {focusMode ? (
          <div className="focus-title">
            <button type="button" className="back-link" onClick={() => navigate(`/${role}`)} aria-label="Back to dashboard" title="Back to dashboard">←</button>
            <div>
              <h2>{providerName} customers</h2>
              <p>{expiryBucket.short}{!loading && ` · ${total} customer${total === 1 ? '' : 's'}`}</p>
            </div>
          </div>
        ) : (
          <h2>Customers</h2>
        )}
        {role !== "operator" && (
          <Select
            options={operatorOptions}
            value={
              operatorOptions.find(op => String(op.value) === String(selectedOperatorId))
              || operatorOptions[0]
            }
            onChange={(selected) =>
              setSelectedOperatorId(
                selected.value === 'all' ? 'all' : Number(selected.value)
              )
            }
            placeholder="Search by phone or name..."
            isSearchable
            styles={{
              container: (base) => ({
                ...base,
                minWidth: 250,
                width: 250
              })
            }}
          />
        )}

        <input
          type="search"
          className="search-input"
          placeholder="Search name, mobile, Internet / DVPL / RailTel username"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          aria-label="Search customers"
        />
        <button className="add-operator-btn" onClick={() => navigate('/add-customer')}>
          + Add Customer
        </button>
      </div>

      {!focusMode && (
      <div className="customer-summary">
        <div className="customer-summary">
          <strong>Total: {summary.total}</strong> |{" "}
          <span>VRPLAY: {activeCount('ExpiryDate')}</span> |{" "}
          <span>Watcho: {activeCount('watcho_expiry')}</span> |{" "}
          <span>Antiptv: {activeCount('antiptv_expiry')}</span> |{" "}
          <span>Playbox TV: {activeCount('playboxtv_expiry')}</span> |{" "}
          <span>PioneerIptv: {activeCount('pioneeriptv_expiry')}</span> |{" "}
          <span>Etvwin: {activeCount('etvwin_expiry')}</span>
        </div>
        <div className="filter-section">
          <label htmlFor="status-filter">Filter by Status:</label>
          <select
            id="status-filter"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="search-input"
          >
            <option value="all">All</option>
            <option value="vr_active">VRPLAY Active</option>
            <option value="vr_inactive">VRPLAY Inactive</option>
            {visibleColumns.watcho && (
              <>
                <option value="watcho_active">Watcho Active</option>
                <option value="watcho_inactive">Watcho Inactive</option>
              </>
            )}
            {visibleColumns.playboxtv && (
              <>
                <option value="ott2_active">Playbox TV Active</option>
                <option value="ott2_inactive">Playbox TV Inactive</option>
              </>
            )}
            {visibleColumns.antiptv && (
              <>
                <option value="antiptv_active">Antiptv Active</option>
                <option value="antiptv_inactive">Antiptv Inactive</option>
              </>
            )}
            {visibleColumns.pioneeriptv && (
              <>
                <option value="pioneeriptv_active">PioneerIptv Active</option>
                <option value="pioneeriptv_inactive">PioneerIptv Inactive</option>
              </>
            )}
            {visibleColumns.etvwin && (
              <>
                <option value="etvwin_active">Etvwin Active</option>
                <option value="etvwin_inactive">Etvwin Inactive</option>
              </>
            )}
          </select>
        </div>
      </div>
      )}

      {expiryBucket && (
        <div className="customers-chip-row">
          <ExpiryFilterChip
            bucketKey={expiryBucket.key}
            providerLabel={providerName}
            count={loading ? null : total}
            onClear={clearExpiryFilter}
          />
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="skeleton-loader">
            {[...Array(8)].map((_, index) => (
              <div key={index} className="skeleton-row">
                {[...Array(7)].map((__, i) => <div key={i} className="skeleton-cell"></div>)}
              </div>
            ))}
          </div>
        </div>
      ) : error ? (
        <LoadError error={errorCause} message={error} onRetry={() => fetchCustomers(page)} />
      ) : (
        <div className="operator-list table-wrapper">
          {filteredCustomers.length > 0 ? (
            <table className="customers-table customers-table--compact">
              <thead>
                <tr>
                  <th>#</th>
                  {/* <th>Username</th> */}
                  <th>Name</th>
                  <th>Phone</th>
                  {shownColumns.map((c) => (
                    <th
                      key={c.field}
                      onClick={() => toggleSort(c.field)}
                      style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                      aria-sort={sort.field === c.field ? (sort.asc ? 'ascending' : 'descending') : 'none'}
                      title="Click to sort"
                    >
                      {c.label} Expiry{sortArrow(c.field)}
                    </th>
                  ))}
                  {user.role !== "operator" && <th>Operator</th>}
                  <th>Remarks</th>
                  {focusMode && <th aria-label="Actions"></th>}
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map((cust, index) => (
                  <tr
                    key={cust.id}
                    className="clickable-row"
                    onClick={() => navigate(customerLink(cust))}
                    title={focusMode ? `Open ${providerName} details` : 'Open customer'}
                  >
                    <td>{(page - 1) * perPage + index + 1}</td>
                    {/* <td>
                      <Link to={customerLink(cust)} onClick={(e) => e.stopPropagation()}>
                        <Highlight text={cust.username} term={searchTerm} />
                      </Link>
                    </td> */}
                    <td className="name-cell">
                      {/* <Link to={customerLink(cust)} onClick={(e) => e.stopPropagation()}> */}
                        <TruncatedText text={`${cust.name || ''} ${cust.lastName || ''}`.trim()} width={180}>
                          <Highlight text={`${cust.name || ''} ${cust.lastName || ''}`} term={searchTerm} />
                        </TruncatedText>
                      {/* </Link> */}
                    </td>
                    <td>
                      <PhoneCell number={cust.MobileNumber}>
                        <Highlight text={cust.MobileNumber} term={searchTerm} />
                      </PhoneCell>
                    </td>
                    {shownColumns.map((c) => (
                      renderExpiryCell(c.field, cust[c.field])
                    ))}
                    {user.role !== "operator" && (
                      <td><TruncatedText text={cust.operatorDetails} width={150} /></td>
                    )}
                    <td>{cust.remarks ? <TruncatedText text={cust.remarks} width={170} /> : ''}</td>
                    {focusMode && (
                      <td>
                        <button
                          type="button"
                          className="open-btn"
                          onClick={(e) => { e.stopPropagation(); navigate(customerLink(cust)); }}
                        >
                          View &amp; renew →
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="no-data">
              <p>No customers match these filters.</p>
              {(expiryBucket || searchTerm || filterStatus !== 'all' || selectedOperatorId !== 'all') && (
                <button
                  className="retry-btn"
                  onClick={() => {
                    setSearchInput('');
                    setFilterStatus('all');
                    setSelectedOperatorId('all');
                    if (expiryBucket) clearExpiryFilter();
                  }}
                >
                  Clear all filters
                </button>
              )}
            </div>
          )}
          {total > 0 && (
            <Pagination page={page} perPage={perPage} total={total} loading={loading}
              onPage={goPage} onPerPage={changePerPage} />
          )}
        </div>
      )}
    </>
  );
};

export default CustomersList;
